import { useState, useCallback, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { LoadingSkeleton } from '@/components/base/LoadingSkeleton';
import { ErrorState } from '@/components/base/ErrorState';
import { radioDomainDataList, getRadioDomainById, type RadioDomainData, type DnsRecord, type DomainStatus } from '@/mocks/radioDomains';
import { saRadiosData } from '@/mocks/superAdminRadios';

const domainStatusLabels: Record<DomainStatus, { label: string; icon: string; colorClass: string; bgClass: string }> = {
  no_configurado: { label: 'No configurado', icon: 'ri-add-circle-line', colorClass: 'text-foreground-400', bgClass: 'bg-background-100' },
  pendiente_dns: { label: 'Pendiente DNS', icon: 'ri-time-line', colorClass: 'text-amber-600', bgClass: 'bg-amber-50' },
  verificado: { label: 'Verificado', icon: 'ri-check-double-line', colorClass: 'text-emerald-600', bgClass: 'bg-emerald-50' },
  activo: { label: 'Activo', icon: 'ri-check-line', colorClass: 'text-emerald-600', bgClass: 'bg-emerald-50' },
  error: { label: 'Error', icon: 'ri-error-warning-line', colorClass: 'text-red-500', bgClass: 'bg-red-50' },
};

const planLabels: Record<string, { label: string; color: string }> = {
  free: { label: 'Free', color: 'bg-foreground-100 text-foreground-700' },
  pro: { label: 'Pro', color: 'bg-sky-100 text-sky-700' },
  premium: { label: 'Premium', color: 'bg-amber-100 text-amber-700' },
};

function DomainPageContent({ domainData }: { domainData: RadioDomainData }) {
  const [domain, setDomain] = useState(domainData.domain || '');
  const [status, setStatus] = useState<DomainStatus>(domainData.domainStatus);
  const [showConfig, setShowConfig] = useState(status === 'no_configurado');
  const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>(domainData.dnsInstructions.length > 0 ? domainData.dnsInstructions : [
    { type: 'CNAME', host: 'www', value: 'radiofrecuenciaweb.com', ttl: 3600 },
    { type: 'TXT', host: '@', value: `rfw-verify=${Math.random().toString(36).substring(2, 14)}`, ttl: 300 },
  ]);
  const [historyEntries] = useState(domainData.verificationHistory);

  const statusInfo = domainStatusLabels[status];
  const planInfo = planLabels[domainData.plan] || planLabels.free;

  const handleAddDomain = useCallback(() => {
    if (!domain.trim()) {
      Swal.fire({
        title: 'Dominio requerido',
        text: 'Ingresá un nombre de dominio válido',
        icon: 'warning',
        confirmButtonText: 'Entendido',
        customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50' },
        buttonsStyling: false,
      });
      return;
    }
    const cleanDomain = domain.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
    setDomain(cleanDomain);
    Swal.fire({
      title: 'Dominio registrado',
      html: `<p style="font-size:13px">Se registró <strong>${cleanDomain}</strong> para <strong>${domainData.radioName}</strong>.</p><p style="font-size:10px;color:#9ca3af;margin-top:4px">La radio debe configurar los registros DNS.</p>`,
      icon: 'success',
      confirmButtonText: 'Ver instrucciones DNS',
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50', htmlContainer: 'text-sm' },
      buttonsStyling: false,
    }).then(() => {
      setStatus('pendiente_dns');
      setShowConfig(false);
    });
  }, [domain, domainData.radioName]);

  const handleVerifyDns = useCallback(() => {
    Swal.fire({
      title: 'Verificando DNS',
      html: `<p style="font-size:13px;color:#6b7280">Consultando registros DNS para <strong>${domain}</strong>...</p>`,
      icon: 'info',
      showConfirmButton: false,
      allowOutsideClick: false,
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' },
    });
    setTimeout(() => {
      Swal.fire({
        title: 'DNS Verificado',
        html: `<p style="font-size:13px">Todos los registros DNS de <strong>${domain}</strong> están correctos.</p>`,
        icon: 'success',
        confirmButtonText: 'Activar dominio',
        showCancelButton: true,
        cancelButtonText: 'Después',
        customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50', cancelButton: 'rounded-lg px-5 py-2 text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600', htmlContainer: 'text-sm' },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          setStatus('activo');
          Swal.fire({ title: 'Dominio Activado', html: `<p style="font-size:13px"><strong>${domain}</strong> ahora redirige a ${domainData.radioName}.</p>`, icon: 'success', timer: 3000, showConfirmButton: false, customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' } });
        } else {
          setStatus('verificado');
        }
      });
    }, 2000);
  }, [domain, domainData.radioName]);

  const handleActivateDomain = useCallback(() => {
    Swal.fire({
      title: 'Activar dominio',
      html: `<p style="font-size:13px">¿Confirmás activar <strong>${domain}</strong> para <strong>${domainData.radioName}</strong>?</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Activar',
      cancelButtonText: 'Cancelar',
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50', cancelButton: 'rounded-lg px-5 py-2 text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600', htmlContainer: 'text-sm' },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setStatus('activo');
        Swal.fire({ title: 'Dominio activado', html: `<p style="font-size:13px"><strong>${domain}</strong> ahora está activo.</p>`, icon: 'success', timer: 2500, showConfirmButton: false, customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' } });
      }
    });
  }, [domain, domainData.radioName]);

  const handleDeactivateDomain = useCallback(() => {
    Swal.fire({
      title: 'Desactivar dominio',
      html: `<p style="font-size:13px">¿Desactivar <strong>${domain}</strong> de <strong>${domainData.radioName}</strong>?</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Desactivar',
      cancelButtonText: 'Cancelar',
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-red-500 text-white', cancelButton: 'rounded-lg px-5 py-2 text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600', htmlContainer: 'text-sm' },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setStatus('verificado');
        Swal.fire({ title: 'Dominio desactivado', html: `<p style="font-size:13px"><strong>${domain}</strong> fue desactivado.</p>`, icon: 'success', timer: 3000, showConfirmButton: false, customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' } });
      }
    });
  }, [domain, domainData.radioName]);

  const handleCopyInstructions = useCallback(() => {
    const text = dnsRecords.map((r) => `Tipo: ${r.type} | Host: ${r.host} | Valor: ${r.value} | TTL: ${r.ttl}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({ title: 'Instrucciones copiadas', text: 'Listo para pegar en la configuración DNS.', icon: 'success', timer: 2000, showConfirmButton: false, customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' } });
    }).catch(() => {
      Swal.fire({ title: 'Instrucciones DNS', html: `<pre style="text-align:left;font-size:11px;background:#f3f4f6;padding:10px;border-radius:8px;overflow-x:auto">${text}</pre>`, icon: 'info', customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' }, buttonsStyling: false, confirmButtonText: 'Cerrar' });
    });
  }, [dnsRecords]);

  const handleEditDomain = useCallback(() => {
    Swal.fire({
      title: 'Cambiar dominio',
      html: '<p style="font-size:12px;color:#6b7280">Esto desvinculará el dominio actual y requerirá nueva verificación DNS.</p>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Si, cambiar',
      cancelButtonText: 'Cancelar',
      customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50', cancelButton: 'rounded-lg px-5 py-2 text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600' },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setStatus('no_configurado');
        setDomain('');
        setShowConfig(true);
        setDnsRecords([
          { type: 'CNAME', host: 'www', value: 'radiofrecuenciaweb.com', ttl: 3600 },
          { type: 'TXT', host: '@', value: `rfw-verify=${Math.random().toString(36).substring(2, 14)}`, ttl: 300 },
        ]);
      }
    });
  }, []);

  if (!domainData.hasDomainFeature && status === 'no_configurado') {
    return (
      <div className="max-w-[800px] mx-auto">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-lock-line text-amber-600 text-lg"></i>
          </div>
          <div>
            <p className="text-amber-800 text-sm font-bold mb-1">Plan sin dominio propio</p>
            <p className="text-amber-700 text-xs leading-relaxed">
              <strong>{domainData.radioName}</strong> tiene plan <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ${planInfo.color}`}>{planInfo.label}</span> — este plan no incluye dominio personalizado. Se requiere Premium para activar esta funcionalidad.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto space-y-6">
      {/* Status banner */}
      {status !== 'no_configurado' && (
        <div className={`rounded-2xl p-4 flex items-center gap-3 border ${statusInfo.bgClass}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${statusInfo.bgClass}`}>
            <i className={`${statusInfo.icon} ${statusInfo.colorClass} text-lg`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-foreground-800 text-sm font-bold">
              {status === 'activo' ? `${domain} — Activo` : statusInfo.label}
            </p>
            <p className="text-foreground-400 text-[11px]">
              {status === 'activo' && 'Dominio redirigiendo correctamente.'}
              {status === 'verificado' && 'DNS verificado. Podés activarlo.'}
              {status === 'pendiente_dns' && 'Esperando configuración DNS del dueño.'}
              {status === 'error' && 'Problema con configuración DNS.'}
            </p>
          </div>
        </div>
      )}

      {/* No configurado — Form */}
      {status === 'no_configurado' && showConfig && (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
          <h3 className="text-foreground-950 font-bold text-sm mb-1">Agregar dominio propio</h3>
          <p className="text-foreground-400 text-[11px] mb-4">Ingresá el dominio a vincular (sin https://).</p>
          <div className="flex gap-3">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="miradio.com"
              className="flex-1 px-4 py-2.5 bg-white border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddDomain(); }}
            />
            <button
              onClick={handleAddDomain}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-sm"></i>
              Agregar
            </button>
          </div>
        </div>
      )}

      {/* DNS Instructions */}
      {status !== 'no_configurado' && (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">Instrucciones DNS</h3>
              <p className="text-foreground-400 text-[10px] mt-0.5">Registros que el dueño debe configurar en su proveedor de dominio</p>
            </div>
            <button onClick={handleCopyInstructions} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-file-copy-line text-xs"></i>
              Copiar todo
            </button>
          </div>
          <div className="space-y-2">
            {dnsRecords.map((record, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-background-100">
                <span className="w-14 h-7 rounded-md bg-foreground-950 text-background-50 text-[10px] font-bold flex items-center justify-center shrink-0">{record.type}</span>
                <div className="flex-1 min-w-0 grid grid-cols-3 gap-2">
                  <div>
                    <p className="text-foreground-400 text-[9px] font-semibold uppercase">Host</p>
                    <p className="text-foreground-800 text-xs font-mono truncate">{record.host}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-foreground-400 text-[9px] font-semibold uppercase">Valor</p>
                    <p className="text-foreground-800 text-xs font-mono truncate">{record.value}</p>
                  </div>
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(record.value).then(() => Swal.fire({ title: 'Copiado', icon: 'success', timer: 1500, showConfirmButton: false, customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm' } })); }}
                  className="w-8 h-8 rounded-lg bg-white border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-50 shrink-0 whitespace-nowrap"
                >
                  <i className="ri-file-copy-line text-foreground-400 text-xs"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {status !== 'no_configurado' && (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
          <h3 className="text-foreground-950 font-bold text-sm mb-4">Acciones</h3>
          <div className="flex flex-wrap gap-2">
            {status === 'pendiente_dns' && (
              <button onClick={handleVerifyDns} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-search-line text-xs"></i>Verificar DNS
              </button>
            )}
            {status === 'verificado' && (
              <button onClick={handleActivateDomain} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-check-line text-xs"></i>Activar dominio
              </button>
            )}
            {status === 'activo' && (
              <button onClick={handleDeactivateDomain} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-close-circle-line text-xs"></i>Desactivar
              </button>
            )}
            {(status === 'verificado' || status === 'activo') && (
              <button onClick={handleVerifyDns} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-refresh-line text-xs"></i>Re-verificar
              </button>
            )}
            <button onClick={handleCopyInstructions} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-file-copy-line text-xs"></i>Copiar instrucciones
            </button>
            <button onClick={handleEditDomain} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-edit-line text-xs"></i>Cambiar dominio
            </button>
          </div>
        </div>
      )}

      {/* History */}
      {historyEntries.length > 0 && (
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
          <h3 className="text-foreground-950 font-bold text-sm mb-4">Historial de verificación</h3>
          <div className="space-y-2">
            {historyEntries.map((entry) => (
              <div key={entry.id} className="flex items-center gap-3 p-3 rounded-xl bg-background-100">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${entry.result === 'success' ? 'bg-emerald-100' : 'bg-red-50'}`}>
                  <i className={`text-sm ${entry.result === 'success' ? 'ri-check-line text-emerald-600' : 'ri-close-line text-red-500'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-foreground-800 text-xs">{entry.message}</p>
                  <p className="text-foreground-400 text-[10px] mt-0.5">{new Date(entry.timestamp).toLocaleString('es', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${entry.result === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                  {entry.result === 'success' ? 'Éxito' : 'Falló'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info: QR no cambia */}
      <div className="bg-accent-50 border border-accent-200 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-information-line text-accent-500 text-sm"></i>
        </div>
        <div>
          <p className="text-accent-800 text-xs font-bold mb-0.5">El QR permanente no cambia</p>
          <p className="text-accent-600/80 text-[11px] leading-relaxed">
            Sin importar si activás, desactivás o cambiás el dominio, el QR siempre apunta a <strong>radiofrecuenciaweb.com/r/{domainData.radioName.toLowerCase().replace(/\s+/g, '-')}</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SARadioDomainPage() {
  const { id } = useParams<{ id: string }>();
  const radioId = parseInt(id || '0', 10);
  const radio = saRadiosData.find((r) => r.id === radioId);
  const domainData = getRadioDomainById(radioId);

  const [pageState, setPageState] = useState<'loading' | 'ready' | 'error'>('loading');

  const loadData = useCallback(() => {
    setPageState('loading');
    setTimeout(() => {
      setPageState('ready');
    }, 500);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (!radio || !domainData) {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-2xl bg-foreground-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-foreground-300 text-3xl"></i>
          </div>
          <h2 className="text-foreground-950 font-bold text-lg mb-2">Radio no encontrada</h2>
          <p className="text-foreground-400 text-sm mb-4">La radio con ID {id} no existe en el sistema.</p>
          <Link to="/sa/radios" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line text-sm"></i>Volver a Radios
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <header className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/sa/radios" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-xs"></i>Radios
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-background-100 overflow-hidden shrink-0">
              <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Dominio de {radio.name}</h2>
              <p className="text-foreground-400 text-xs">{radio.frequency} · <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ${planLabels[radio.plan]?.color || ''}`}>{planLabels[radio.plan]?.label || radio.plan}</span></p>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center gap-3 px-4 h-14">
          <Link to="/sa/radios" className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-arrow-left-line text-foreground-500 text-sm"></i>
          </Link>
          <div className="w-8 h-8 rounded-lg bg-background-100 overflow-hidden shrink-0">
            <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <h1 className="text-foreground-950 font-bold text-sm truncate">Dominio — {radio.name}</h1>
            <p className="text-foreground-400 text-[9px]">{radio.frequency} · {planLabels[radio.plan]?.label || radio.plan}</p>
          </div>
        </div>
      </header>

      <div className="p-4 md:p-6 lg:p-8 pb-28 lg:pb-8">
        {pageState === 'loading' && <LoadingSkeleton variant="detail" />}
        {pageState === 'error' && <ErrorState title="Error al cargar dominio" message="No pudimos obtener los datos del dominio. Intentá de nuevo." onRetry={loadData} />}
        {pageState === 'ready' && (
        <DomainPageContent domainData={domainData} />
        )}
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-background-50/98 backdrop-blur-2xl border-t border-background-200">
        <div className="flex items-center justify-around h-[56px] px-1">
          {[
            { icon: 'ri-dashboard-line', label: 'Dashboard', href: '/sa/dashboard' },
            { icon: 'ri-radio-line', label: 'Radios', href: '/sa/radios' },
            { icon: 'ri-radar-line', label: 'Frecuencias', href: '/sa/frequencies' },
            { icon: 'ri-file-list-3-line', label: 'Planes', href: '/sa/plans' },
            { icon: 'ri-global-line', label: 'Dominio', active: true },
          ].map((item) => (
            <Link
              key={item.label}
              to={item.href || '#'}
              className={`flex flex-col items-center justify-center gap-0.5 h-full min-w-0 flex-1 cursor-pointer transition-colors relative ${item.active ? 'text-primary-600' : 'text-foreground-400'}`}
            >
              <i className={`${item.icon} text-xl`}></i>
              <span className="text-[10px] font-semibold">{item.label}</span>
              {item.active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary-500 rounded-full"></span>}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}